import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Paper,
  Alert,
  Snackbar,
  MenuItem,
  Chip,
  Fade,
  Slide,
  Avatar,
  useTheme,
  alpha
} from '@mui/material';
import {
  Send,
  AccessTime,
  ArrowForward,
  LocationOn
} from '@mui/icons-material';
import { officeHours, getContactMethods, timeOptions, methodOptions } from '../config/contactConfig';

const Contact = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    bestTimeToContact: '',
    preferredMethod: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Contact form submitted:', formData);
    setShowSuccess(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: '',
      bestTimeToContact: '',
      preferredMethod: ''
    });
  };

  // Use contact methods from global config
  const contactMethods = getContactMethods(theme);

  return (
    <Box>
      {/* Modern Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.95)} 0%, ${alpha(theme.palette.primary.main, 0.9)} 100%), url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000"><polygon fill="${alpha('#ffffff', 0.1)}" points="0,1000 1000,0 1000,1000"/></svg>')`,
          color: '#f5f5f5',
          py: { xs: 10, md: 12 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg" sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Fade in timeout={1000}>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)'
                }}
              >
                Contact Us
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  opacity: 0.9,
                  fontWeight: 300,
                  maxWidth: 700,
                  mx: 'auto',
                  mb: 4,
                  lineHeight: 1.6
                }}
              >
                Get in touch with Eagles Refuge Academy and discover how we can help your child thrive
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Box sx={{ py: 10, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg" sx={{ width: '100%' }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="When to Reach Us"
                sx={{
                  mb: 3,
                  px: 3,
                  py: 1,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  color: 'warning.main',
                  fontWeight: 600,
                  fontSize: '1rem'
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: '2.2rem', md: '3rem' },
                  color: 'text.primary'
                }}
              >
                Office Hours
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 700, mx: 'auto', fontSize: '1.2rem', lineHeight: 1.6, mb: 5 }}
              >
                Our administrative team is available during these hours to assist you
              </Typography>
            </Box>
          </Fade>

          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            <Fade in timeout={1000}>
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  borderRadius: 4,
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  borderTop: `4px solid ${theme.palette.primary.main}`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <AccessTime sx={{ fontSize: 28, color: theme.palette.primary.main, mr: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Office Hours
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                    {officeHours.weekdays.days}
                  </Typography>
                  <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                    {officeHours.weekdays.hours}
                  </Typography>
                </Box>
              </Paper>
            </Fade>
          </Box>
        </Container>
      </Box>

      <Box sx={{ py: 12, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip
                label="Get In Touch"
                sx={{
                  mb: 3,
                  px: 3,
                  py: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  fontWeight: 600,
                  fontSize: '1rem'
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                How to Reach Us
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 700, mx: 'auto', fontSize: '1.2rem', lineHeight: 1.6 }}
              >
                We're here to answer your questions and provide the information you need
              </Typography>
            </Box>
          </Fade>

          <Box 
            sx={{ 
              maxWidth: 1200, 
              mx: 'auto',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 4,
              '@media (max-width: 600px)': {
                gridTemplateColumns: '1fr'
              }
            }}>
            {/* Create an array of 3 columns, filling with contact methods or empty placeholders */}
            {Array(3).fill(null).map((_, colIndex) => {
              const method = contactMethods[colIndex];
              return (
                <Box key={colIndex} sx={{ width: '100%' }}>
                  {method ? (
                    <Fade in timeout={1000 + (colIndex * 200)}>
                      <Card
                        elevation={3}
                        sx={{
                          height: '100%',
                          minHeight: 240,
                          borderRadius: 4,
                          overflow: 'hidden',
                          position: 'relative',
                          transition: 'all 0.4s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-12px)',
                            boxShadow: 8,
                            '&::before': {
                              transform: 'translateY(0)'
                            }
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: `linear-gradient(90deg, ${method.color}, ${alpha(method.color, 0.7)})`,
                            transform: 'translateY(-4px)',
                            transition: 'transform 0.3s ease'
                          }
                        }}
                      >
                        <CardContent sx={{
                          p: 4,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center'
                        }}>
                          <Avatar
                            sx={{
                              bgcolor: alpha(method.color, 0.1),
                              color: method.color,
                              width: 80,
                              height: 80,
                              mb: 3
                            }}
                          >
                            {React.cloneElement(method.icon, { sx: { fontSize: 40 } })}
                          </Avatar>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontWeight: 700, 
                              mb: 2,
                              color: method.color
                            }}
                          >
                            {method.title}
                          </Typography>
                          <Box>
                            {method.details.map((detail, idx) => (
                              <Typography
                                key={idx}
                                variant="body1"
                                sx={{ 
                                  mb: 0.5,
                                  fontSize: '1.1rem',
                                  fontWeight: 500,
                                  color: 'text.secondary'
                                }}
                              >
                                {detail}
                              </Typography>
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Fade>
                  ) : (
                    <Card
                      elevation={3}
                      sx={{
                        height: '100%',
                        minHeight: 240,
                        width: '100%',
                        display: 'block',
                        borderRadius: 4,
                        overflow: 'hidden',
                        position: 'relative',
                        transition: 'all 0.4s ease-in-out',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background: `linear-gradient(90deg, ${theme.palette.grey[500]}, ${alpha(theme.palette.grey[500], 0.7)})`,
                          transform: 'translateY(-4px)',
                          transition: 'transform 0.3s ease'
                        }
                      }}
                    >
                      <CardContent sx={{
                        p: 4,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        opacity: 0 // Hide content but maintain layout space
                      }}>
                        {/* Empty placeholder with same structure as real cards */}
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                            color: theme.palette.grey[500],
                            width: 80,
                            height: 80,
                            mb: 3
                          }}
                        >
                          <LocationOn sx={{ fontSize: 40 }} />
                        </Avatar>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: 700, 
                            mb: 2,
                            color: theme.palette.grey[500]
                          }}
                        >
                          Placeholder
                        </Typography>
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{ 
                              mb: 0.5,
                              fontSize: '1.1rem',
                              fontWeight: 500,
                              color: 'text.secondary'
                            }}
                          >
                            Placeholder Text
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              );
            })}
          </Box>
        </Container>
      </Box>

      <Box sx={{ py: 15, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="Contact Form"
                sx={{
                  mb: 3,
                  px: 3,
                  py: 1,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  color: 'success.main',
                  fontWeight: 600,
                  fontSize: '1rem'
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' }
                }}
              >
                Send Us a Message
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 700, mx: 'auto', fontSize: '1.2rem', lineHeight: 1.6 }}
              >
                Fill out the form below and we'll get back to you as soon as possible
              </Typography>
            </Box>
          </Fade>

          <Fade in timeout={1000}>
            <Paper
              elevation={4}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: 4,
                maxWidth: 800,
                mx: 'auto',
                position: 'relative',
                overflow: 'hidden',
                background: 'white',
                boxShadow: `0 10px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: `linear-gradient(135deg, ${alpha('#fff', 0.1)} 0%, transparent 50%)`,
                  pointerEvents: 'none'
                }
              }}
            >
              <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* Personal Information Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                    Your Information
                  </Typography>
                  <Grid container spacing={4}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        InputProps={{
                          sx: { borderRadius: 3 }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        InputProps={{
                          sx: { borderRadius: 3 }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        variant="outlined"
                        placeholder="(555) 123-4567"
                        InputProps={{
                          sx: { borderRadius: 3 }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Contact Preferences Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                    Contact Preferences
                  </Typography>
                  <Box 
                    sx={{ 
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 4,
                      '@media (max-width: 600px)': {
                        gridTemplateColumns: '1fr'
                      }
                    }}>
                    <Box>
                      <TextField
                        select
                        fullWidth
                        label="Best Time to Contact"
                        name="bestTimeToContact"
                        value={formData.bestTimeToContact}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                          sx: { borderRadius: 3 }
                        }}
                      >
                        {timeOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box>
                      <TextField
                        select
                        fullWidth
                        label="Preferred Contact Method"
                        name="preferredMethod"
                        value={formData.preferredMethod}
                        onChange={handleChange}
                        variant="outlined"
                        sx={{ minWidth: '240px' }}
                        InputProps={{
                          sx: { borderRadius: 3 }
                        }}
                      >
                        {methodOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box>
                      {/* Empty third column to maintain 3-column layout */}
                    </Box>
                  </Box>
                </Box>

                {/* Message Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                    Your Message
                  </Typography>
                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    multiline
                    rows={8}
                    variant="outlined"
                    placeholder="Please tell us how we can help you..."
                    InputProps={{
                      sx: { borderRadius: 3 }
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    endIcon={<Send />}
                    sx={{
                      px: 8,
                      py: 2,
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      borderRadius: 3,
                      boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: `0 12px 25px ${alpha(theme.palette.primary.main, 0.4)}`
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Send Message
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Container>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Thank you for your message! We will get back to you soon.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
